import React from "react";

import { observer } from "mobx-react-lite";

import { ModalOpenPDF } from "@synthesis/pdf-viewer";
import { IconPlus } from "@synthesis/shared-icons";
import { Button, Checkbox, TabGroup, type TabGroupTab, Toolbar, ToolbarContextProvider, useToolbar } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import type { FileMetaDto } from "./api";

import { DragAndDrop, Explorer, Redirect } from "./components";
import { DrawingsCollection } from "./components/drawings-collection";
import { ModalReName } from "./components/explorer/local-components";
import { ModalIntegrationProject } from "./components/files";
import { ModalDeleteFile, ModalMarkupPreview } from "./components/files/local-components";
import ModalPreview from "./components/files/local-components/modal-preview";
import { ModalMeta } from "./components/meta-data";
import { AddFile } from "./components/toolbar/local-components";
import { UploadProgressProvider } from "./file-uploader/file-uploader";
import { useMarkUp } from "./components/files/hooks";
import useToolbarActions from "./hooks/use-toolbar-actions";
import { ExplorerView, FileSystemWorkerProvider, StoreProvider, useStores } from "./store";

export type FileSystemRolesGGE = {
  СУПЕР: boolean;
  АДМИНИСТРИРОВАНИЕ: boolean;
  МОДЕРИРОВАНИЕ: boolean;
  ПРОСМОТР: boolean;
  НЕТ: boolean;
};

export type FileSystemTab = "models" | "docs" | "drawings";

export type FileSystemProps = {
  onlyModels?: boolean;
  className?: string;
  projectId: string;
  style?: React.CSSProperties;
  rolesGGE?: FileSystemRolesGGE;
  isIntegration?: boolean;
  archivedProject?: boolean;
  fixedTab?: FileSystemTab;
  hideTabs?: boolean;
};

const tabs: TabGroupTab[] = [
  {
    title: "Трёхмерные модели",
    value: "Трёхмерные модели",
    key: "Трёхмерные модели",
  },
  {
    title: "Документация",
    value: "Документация",
    key: "Документация",
  },
  {
    title: "Коллекция чертежей",
    value: "Коллекция чертежей",
    key: "Коллекция чертежей",
  },
];

const TAB_TITLES: Record<FileSystemTab, string> = {
  models: "Трёхмерные модели",
  docs: "Документация",
  drawings: "Коллекция чертежей",
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Б";
  const kb = 1024;
  const mb = kb * 1024;
  const gb = mb * 1024;
  if (bytes < mb) return `${(bytes / kb).toFixed(1)} Кб`;
  if (bytes < gb) return `${(bytes / mb).toFixed(1)} Мб`;
  return `${(bytes / gb).toFixed(2)} Гб`;
};

const MarkupModalWrapper = ({ markupFile, isOpen, setIsOpen }: { markupFile: FileMetaDto; isOpen: boolean; setIsOpen: (v: boolean) => void }) => {
  const { markupsCount } = useMarkUp(markupFile.id);
  return (
    <ModalMarkupPreview
      sourceId={markupFile.sourceId}
      modelId={markupFile.id}
      markupsCount={markupsCount ?? 0}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      openModal={() => setIsOpen(true)}
      closeModal={() => setIsOpen(false)}
      file={markupFile}
    />
  );
};

const FileSystemContent = observer<FileSystemProps>(
  ({ onlyModels: _onlyModels, className, style, rolesGGE, archivedProject, isIntegration, projectId: _projectId, fixedTab, hideTabs }) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const { generalStore } = useStores();
    const { fileStore } = generalStore;
    const { action, option } = useToolbar();
    const [selectedFile, setSelectedFile] = React.useState<FileMetaDto>();
    const [isOpenRename, setIsOpenRename] = React.useState(false);
    const [isModalIntegration, setIsModalIntegration] = React.useState({
      isOpen: false,
      addFile: false,
      rename: false,
      delete: false,
      updateVersion: false,
    });
    const [isOpenDeleteFile, setIsOpenDeleteFile] = React.useState(false);
    const [selectedPdfFile, setSelectedPdfFile] = React.useState<FileMetaDto>();
    const [isOpenPdfModal, setIsOpenPdfModal] = React.useState(false);
    const [isOpenMeta, setIsOpenMeta] = React.useState(false);
    const [metaFile, setMetaFile] = React.useState<FileMetaDto | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
    const [previewModels, setPreviewModels] = React.useState<string>("");
    const [isMarkupOpen, setIsMarkupOpen] = React.useState(false);
    const [markupFile, setMarkupFile] = React.useState<FileMetaDto | undefined>();
    const isSuperOrAdmin = (rolesGGE && rolesGGE.СУПЕР) || (rolesGGE && rolesGGE.АДМИНИСТРИРОВАНИЕ);
    const isModeratorAndNotIntegration = rolesGGE && rolesGGE.МОДЕРИРОВАНИЕ && !isIntegration;
    const disabledRight = (!isSuperOrAdmin && !isModeratorAndNotIntegration) || archivedProject;
    const openPdfModal = (file: FileMetaDto) => {
      setSelectedPdfFile(file);
      setIsOpenPdfModal(true);
    };
    const selectedPdfPageCount = selectedPdfFile ? (generalStore.pdfPreviewsByFileId.get(selectedPdfFile.id)?.pageCount ?? 0) : 0;

    const { handleAddLeft, handleAddRight, uploadFileRequest, closeModalDeleteFile, patchModelIdFromGantt } = useToolbarActions(
      option,
      disabledRight,
      action,
      setSelectedFile,
      setIsOpenRename,
      inputRef,
      setIsOpenDeleteFile,
      isIntegration,
      setIsModalIntegration,
      setSelectedPdfFile,
      setIsOpenPdfModal,
      setIsOpenMeta,
      setMetaFile,
      setPreviewModels,
      setIsMarkupOpen,
      setMarkupFile,
    );

    if (generalStore.activeTab === "docs") {
      if (generalStore.checkedFiles.size === 0) {
        option?.disableMany(["docs:open", "docs:download"]);
      } else {
        option?.enableMany(["docs:download"]);
      }
      if (generalStore.checkedFiles.size !== 1) {
        option?.disableMany(["docs:open"]);
      } else {
        option?.enableMany(["docs:open"]);
      }
      if (generalStore.checkedFiles.size === 0 || disabledRight) {
        option?.disableMany(["docs:delete"]);
      } else {
        option?.enableMany(["docs:delete"]);
      }
      if (generalStore.checkedFiles.size !== 1 || disabledRight) {
        option?.disableMany(["docs:rename"]);
      } else {
        option?.enableMany(["docs:rename"]);
      }
    } else if (generalStore.activeTab === "models") {
      if (generalStore.checkedFiles.size === 0) {
        option?.disableMany(["download"]);
      } else {
        option?.enableMany(["download"]);
      }
      if (generalStore.checkedFiles.size === 0 || disabledRight) {
        option?.disableMany(["deleteKey"]);
      } else {
        option?.enableMany(["deleteKey"]);
      }
      if (generalStore.checkedFiles.size !== 1 || disabledRight) {
        option?.disableMany(["renameKey", "uploadVersionKey"]);
      } else {
        option?.enableMany(["renameKey", "uploadVersionKey"]);
      }
      if (generalStore.checkedFiles.size !== 1) {
        option?.disableMany(["filesystem:markup", "filesystem:file-info", "filesystem:model-open"]);
      } else {
        option?.enableMany(["filesystem:markup", "filesystem:file-info", "filesystem:model-open"]);
      }
      if (generalStore.checkedFiles.size < 2) {
        option?.disableMany(["filesystem:model-open-assembly"]);
      } else {
        option?.enableMany(["filesystem:model-open-assembly"]);
      }
    }

    React.useEffect(() => {
      if (action && option) {
        handleAddLeft();
        handleAddRight();
      }
    }, [action, option, generalStore.activeTab, handleAddLeft, handleAddRight]);

    React.useEffect(() => {
      generalStore.resetCheckedAll();
    }, [generalStore.activeTab, generalStore]);

    React.useEffect(() => {
      if (fixedTab && generalStore.activeTab !== fixedTab) {
        generalStore.setActiveTab(fixedTab);
      }
    }, [fixedTab, generalStore]);

    const isAllChecked = React.useMemo(() => {
      const allFiles = [...generalStore.directoryStore.directories, ...generalStore.fileStore.visibleFiles];
      const checkedFilesSize = generalStore.checkedFiles.size;

      if (checkedFilesSize === 0) return false;
      if (checkedFilesSize === allFiles.length) return true;
      return "indeterminate";
    }, [generalStore.directoryStore.directories, generalStore.fileStore.visibleFiles, generalStore.checkedFiles.size]);

    const selectedFilesInfo = React.useMemo(() => {
      const checkedIds = Array.from(generalStore.checkedFiles.keys());
      const checkedFiles = fileStore.visibleFiles.filter((f) => checkedIds.includes(f.id));
      const totalSize = checkedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
      return { count: checkedFiles.length, size: totalSize };
    }, [generalStore.checkedFiles, fileStore.visibleFiles]);

    const handleChangeChecked = () => {
      const allFiles = [...generalStore.directoryStore.directories, ...generalStore.fileStore.visibleFiles];
      const checkedFilesSize = generalStore.checkedFiles.size;

      if (checkedFilesSize === allFiles.length) {
        generalStore.resetCheckedAll();
      } else {
        generalStore.setCheckedAll();
      }
    };

    const tabValueMap: Record<string, "models" | "docs" | "drawings"> = {
      "Трёхмерные модели": "models",
      Документация: "docs",
      "Коллекция чертежей": "drawings",
    };

    const handleTabChange = (tab: string) => {
      const nextTab = tabValueMap[tab];
      if (nextTab) {
        generalStore.setActiveTab(nextTab);
      }
    };

    return (
      <Container className={cn(className, generalStore.view === ExplorerView.Table && "pb-2")} style={style}>
        <header className="flex justify-between">
          <h1 data-cy="fileSystem-h1-projectDocks" className="text-lg font-bold">
            {fixedTab ? TAB_TITLES[fixedTab] : "Загруженные материалы"}
          </h1>
          <div className="flex gap-2">
            {generalStore.activeTab !== "drawings" &&
              (!isIntegration ? (
                <AddFile disabledRight={disabledRight} />
              ) : (
                <Button
                  size="sm"
                  leftIcon={<IconPlus className="size-5" />}
                  disabled={disabledRight}
                  theme="secondary"
                  onClick={() => setIsModalIntegration((prevState) => ({ ...prevState, addFile: true, isOpen: true }))}
                >
                  Добавить файл
                </Button>
              ))}
          </div>
        </header>

        {!hideTabs && (
          <TabGroup tabs={tabs} activeKey={tabs.find((t) => tabValueMap[t.value] === generalStore.activeTab)?.value} onChange={handleTabChange} />
        )}

        {generalStore.activeTab === "models" && (
          <>
            <Toolbar.Root className="h-12" dropdownAutoClose />
            {generalStore.view !== ExplorerView.Table && (
              <div data-cy="fileSystem-div" className="flex gap-2 pl-4">
                <Checkbox data-cy="fileSystem-checkbox" checked={isAllChecked} onChange={handleChangeChecked} />
                Выбрать все файлы
              </div>
            )}
            <div
              className={cn(
                "relative h-full",
                generalStore.view !== ExplorerView.Table && "h-[calc(100%-8rem)]",
                generalStore.view === ExplorerView.Grid && "min-h-0 flex-1",
              )}
            >
              <div className={cn("relative h-full flex-1", generalStore.view === ExplorerView.Grid && "min-h-0")}>
                <Explorer disabledRight={disabledRight} isIntegration={isIntegration} openPdfModal={openPdfModal} />
                <DragAndDrop />
                {generalStore.view === ExplorerView.Grid && (
                  <div className="mt-2 border-t-2 pl-3 pt-1">
                    Файлов: {fileStore.visibleFiles.length}{" "}
                    {selectedFilesInfo.count > 0 && `Выбрано: ${selectedFilesInfo.count} (${formatFileSize(selectedFilesInfo.size)})`}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {generalStore.activeTab === "docs" && (
          <>
            <Toolbar.Root className="h-12" dropdownAutoClose />
            {generalStore.view !== ExplorerView.Table && (
              <div data-cy="fileSystem-div" className="flex gap-2 pl-4">
                <Checkbox data-cy="fileSystem-checkbox" checked={isAllChecked} onChange={handleChangeChecked} />
                Выбрать все файлы
              </div>
            )}
            <div
              className={cn(
                "relative h-full",
                generalStore.view !== ExplorerView.Table && "h-[calc(100%-8rem)]",
                generalStore.view === ExplorerView.Grid && "min-h-0 flex-1",
              )}
            >
              <div className={cn("relative h-full flex-1", generalStore.view === ExplorerView.Grid && "min-h-0")}>
                <Explorer disabledRight={disabledRight} isIntegration={isIntegration} openPdfModal={openPdfModal} />
                <DragAndDrop />
                {generalStore.view === ExplorerView.Grid && (
                  <div className="mt-2 border-t-2 pl-3 pt-1">
                    Файлов: {fileStore.visibleFiles.length}{" "}
                    {selectedFilesInfo.count > 0 && `Выбрано: ${selectedFilesInfo.count} (${formatFileSize(selectedFilesInfo.size)})`}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {generalStore.activeTab === "drawings" && <DrawingsCollection disabledRight={disabledRight} />}

        <Redirect />

        <ModalReName
          name={selectedFile?.baseName ?? ""}
          id={selectedFile?.id ?? ""}
          type={selectedFile?.type ?? ""}
          isOpen={isOpenRename}
          setIsOpen={setIsOpenRename}
          openModal={() => setIsOpenRename(true)}
          closeModal={() => setIsOpenRename(false)}
        />

        <ModalDeleteFile
          deleteId={selectedFile?.id}
          isOpen={isOpenDeleteFile}
          setIsOpen={setIsOpenDeleteFile}
          openModal={() => setIsOpenDeleteFile(true)}
          closeModal={closeModalDeleteFile}
        />

        <ModalOpenPDF
          fileId={selectedPdfFile?.id}
          fileName={selectedPdfFile?.baseName + "." + selectedPdfFile?.ext}
          pageCount={selectedPdfPageCount}
          projectId={generalStore.projectId ?? undefined}
          isOpen={isOpenPdfModal}
          setIsOpen={setIsOpenPdfModal}
          openModal={() => setIsOpenPdfModal(true)}
          closeModal={() => setIsOpenPdfModal(false)}
        />

        <ModalIntegrationProject
          isOpen={isModalIntegration.isOpen}
          setIsOpen={(newState) => setIsModalIntegration((prevState) => ({ ...prevState, isOpen: newState }))}
          openModal={() => setIsModalIntegration((prevState) => ({ ...prevState, isOpen: true }))}
          closeModal={() =>
            setIsModalIntegration(() => ({
              addFile: false,
              rename: false,
              isOpen: false,
              updateVersion: false,
              delete: false,
            }))
          }
          isModalIntegration={isModalIntegration}
          disabledRight={disabledRight}
          setIsOpenRename={setIsOpenRename}
          setIsOpenDeleteFile={setIsOpenDeleteFile}
          setIsModalIntegration={setIsModalIntegration}
          uploadFileRequest={() => inputRef.current?.click()}
        />

        {metaFile && (
          <ModalMeta
            onUpdateVersion={(newFileId: string) => {
              if (metaFile.id) {
                patchModelIdFromGantt(metaFile.id, newFileId);
              }
            }}
            info={metaFile}
            selectedFile={metaFile}
            onClickModel={() => setIsPreviewOpen(true)}
            isOpen={isOpenMeta}
            setIsOpen={setIsOpenMeta}
            openModal={() => setIsOpenMeta(true)}
            closeModal={() => {
              setIsOpenMeta(false);
              setMetaFile(null);
            }}
          />
        )}
        <ModalPreview
          modelId={previewModels}
          isOpen={isPreviewOpen}
          setIsOpen={setIsPreviewOpen}
          openModal={() => setIsPreviewOpen(true)}
          closeModal={() => setIsPreviewOpen(false)}
        />
        {markupFile && <MarkupModalWrapper markupFile={markupFile} isOpen={isMarkupOpen} setIsOpen={setIsMarkupOpen} />}

        <input data-cy="files-input-projectDocks" type="file" id="file" className="hidden" ref={inputRef} onChange={uploadFileRequest} />
      </Container>
    );
  },
);

const FileSystem = React.memo<FileSystemProps>(
  ({ onlyModels, className, style, projectId, rolesGGE, isIntegration, archivedProject, fixedTab, hideTabs }) => {
    return (
      <StoreProvider onlyModels={onlyModels} projectId={projectId}>
        <ToolbarContextProvider>
          <FileSystemWorkerProvider>
            <UploadProgressProvider>
              <FileSystemContent
                rolesGGE={rolesGGE}
                onlyModels={onlyModels}
                className={className}
                style={style}
                projectId={projectId}
                isIntegration={isIntegration}
                archivedProject={archivedProject}
                fixedTab={fixedTab}
                hideTabs={hideTabs}
              />
            </UploadProgressProvider>
          </FileSystemWorkerProvider>
        </ToolbarContextProvider>
      </StoreProvider>
    );
  },
);
type ContainerProps = React.HTMLAttributes<HTMLDivElement>;

const Container = ({ className, style, ...props }: ContainerProps) => (
  <div
    className={cn("border-grey-100 relative z-0 mt-8 flex h-full min-h-0 flex-col gap-6 overflow-hidden border bg-white p-6", className)}
    style={style}
    {...props}
  />
);

export default FileSystem;
